import React, { useState, useEffect, useRef } from 'react';
import { XMarkIcon, MagnifyingGlassIcon, UserPlusIcon, SparklesIcon, ChartBarIcon, DocumentTextIcon, ClockIcon } from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/24/solid';
import axios from 'axios';
import { useTheme } from '../../features/theme/context/ThemeContext';

const CreateProjectModal = ({ isOpen, onClose, onProjectCreated }) => {
  const { darkMode } = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isPrivate: false
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [wasManuallyEnhanced, setWasManuallyEnhanced] = useState(false);
  const [error, setError] = useState('');
  const [showMemberSearch, setShowMemberSearch] = useState(false);
  const searchTimeoutRef = useRef(null);
  const modalRef = useRef(null);
  const isInitialMount = useRef(true);

  // Storage key for persistence
  const STORAGE_KEY = 'createProjectModal';

  // Load saved state on component mount
  useEffect(() => {
    const savedState = localStorage.getItem(STORAGE_KEY);
    console.log('Modal checking for saved state:', savedState);
    
    if (savedState) {
      try {
        const parsedState = JSON.parse(savedState);
        console.log('Modal parsed state:', parsedState);
        
        if (parsedState.isOpen) {
          console.log('Restoring modal form data');
          // Restore form data
          setFormData(parsedState.formData || { name: '', description: '', isPrivate: false });
          setSelectedMembers(parsedState.selectedMembers || []);
          setShowMemberSearch(parsedState.showMemberSearch || false);
          setSearchTerm(parsedState.searchTerm || '');
          // Don't restore isOpen state here - let the parent component handle it
        }
      } catch (error) {
        console.error('Error parsing saved project modal state:', error);
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, []);

  // Save state to localStorage whenever relevant state changes
  useEffect(() => {
    if (isOpen) {
      const stateToSave = {
        isOpen: true,
        formData,
        selectedMembers,
        showMemberSearch,
        searchTerm
      };
      
      console.log('Saving modal state:', stateToSave);
      
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
      } catch (error) {
        console.error('Error saving project modal state:', error);
      }
    }
  }, [isOpen, formData, selectedMembers, showMemberSearch, searchTerm]);

  // Clear saved state when modal is closed or project is created
  const clearSavedState = () => {
    localStorage.removeItem(STORAGE_KEY);
  };

  // Form validation
  const isFormValid = formData.name.trim() && formData.description.trim();

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      // Only reset if there's no saved state to restore
      const savedState = localStorage.getItem(STORAGE_KEY);
      if (!savedState || !JSON.parse(savedState).isOpen) {
        setFormData({ name: '', description: '', isPrivate: false });
        setSearchTerm('');
        setSearchResults([]);
        setSelectedMembers([]);
        setError('');
        setShowMemberSearch(false);
      }
      isInitialMount.current = false;
    } else if (!isInitialMount.current) {
      // Only clear saved state when modal is closed by user action, not on initial mount
      console.log('Modal closed by user, clearing saved state');
      clearSavedState();
    }
  }, [isOpen]);

  // Focus trap for accessibility
  useEffect(() => {
    if (isOpen && modalRef.current) {
      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusableElements.length > 0) {
        focusableElements[0].focus();
      }
    }
  }, [isOpen]);

  // Search for organization members
  const searchMembers = async (term) => {
    if (!term.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/projects/organization/members?search=${encodeURIComponent(term)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Get current user ID from token
      let currentUserId = null;
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        currentUserId = payload.userId || payload.id;
      } catch (error) {
        console.error('Error decoding token:', error);
      }
      
      // Filter out already selected members and current user
      const selectedIds = selectedMembers.map(m => m.id);
      const filteredResults = response.data.members.filter(member => 
        !selectedIds.includes(member.id) && member.id !== currentUserId
      );
      
      setSearchResults(filteredResults);
    } catch (error) {
      console.error('Error searching members:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchMembers(searchTerm);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, selectedMembers]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Reset manual enhancement flag if user modifies description after enhancement
    if (name === 'description' && wasManuallyEnhanced) {
      setWasManuallyEnhanced(false);
    }
  };

  const handleMemberSelect = (member) => {
    setSelectedMembers(prev => [...prev, member]);
    setSearchTerm('');
    setSearchResults([]);
  };

  const handleMemberRemove = (memberId) => {
    setSelectedMembers(prev => prev.filter(m => m.id !== memberId));
  };

  const handleEnhanceDescription = async () => {
    if (!formData.description.trim()) {
      setError('Please enter a description to enhance');
      return;
    }

    setIsEnhancing(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/projects/enhance-description`,
        {
          description: formData.description.trim()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setFormData(prev => ({
        ...prev,
        description: response.data.enhancedDescription
      }));
      
      // Mark as manually enhanced
      setWasManuallyEnhanced(true);
    } catch (error) {
      console.error('Error enhancing description:', error);
      setError('Failed to enhance description. Please try again.');
    } finally {
      setIsEnhancing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    setError('');

    try {
      let finalDescription = formData.description.trim();
      
      // Auto-enhance description if it wasn't manually enhanced
      if (!wasManuallyEnhanced && finalDescription) {
        try {
          const token = localStorage.getItem('token');
          const enhanceResponse = await axios.post(
            `${process.env.REACT_APP_API_URL}/projects/enhance-description`,
            { description: finalDescription },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          finalDescription = enhanceResponse.data.enhancedDescription;
        } catch (enhanceError) {
          console.error('Auto-enhance failed, using original description:', enhanceError);
          // Continue with original description if auto-enhance fails
        }
      }

      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/projects`,
        {
          name: formData.name.trim(),
          description: finalDescription,
          memberIds: selectedMembers.map(member => member.id),
          isPrivate: formData.isPrivate
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      onProjectCreated(response.data.project);
      onClose();
      
      // Reset form
      setFormData({ name: '', description: '', isPrivate: false });
      setSelectedMembers([]);
      setWasManuallyEnhanced(false);
    } catch (error) {
      console.error('Error creating project:', error);
      setError(error.response?.data?.message || 'Failed to create project');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', isPrivate: false });
    setSelectedMembers([]);
    setSearchTerm('');
    setSearchResults([]);
    setShowMemberSearch(false);
    setWasManuallyEnhanced(false);
    setError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onKeyDown={handleKeyDown}
    >
      <div 
        ref={modalRef}
        className="bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-[#444444] rounded-3xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden"
        role="dialog"
        aria-labelledby="modal-title"
        aria-modal="true"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-[#444444]">
          <h2 id="modal-title" className="text-xl font-semibold text-gray-900 dark:text-white">
            Create New Project
          </h2>
          <button
            onClick={handleClose}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="Close modal"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Project Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Project Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter project name"
                autoComplete="off"
                maxLength={100}
                className="w-full px-4 py-3 border border-gray-300 dark:border-[#444444] rounded-xl focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent bg-white dark:bg-[#181818] text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                required
              />
              <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                {formData.name.length}/100 characters
              </div>
            </div>

            {/* Project Description */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Description *
                </label>
                <button
                  type="button"
                  onClick={handleEnhanceDescription}
                  disabled={isEnhancing || !formData.description.trim()}
                  className="inline-flex items-center px-3 py-1 text-sm text-black dark:text-white hover:text-gray-700 dark:hover:text-gray-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isEnhancing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin mr-1"></div>
                      Enhancing...
                    </>
                  ) : (
                    <>
                      <SparklesIcon className="w-4 h-4 mr-1" />
                      Enhance
                    </>
                  )}
                </button>
              </div>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Describe the project's goals and objectives"
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 dark:border-[#444444] rounded-xl focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent bg-white dark:bg-[#181818] text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 resize-none"
                required
              />
            </div>

            {/* Privacy Setting */}
            <div className="flex items-center">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  id="isPrivate"
                  name="isPrivate"
                  checked={formData.isPrivate}
                  onChange={handleInputChange}
                  className="sr-only peer"
                />
                <div className={`w-[20px] h-[20px] rounded-full border flex items-center justify-center transition-all ${
                  formData.isPrivate 
                    ? 'bg-black dark:bg-white border-black dark:border-white' 
                    : 'bg-white dark:bg-[#282828] border-[#727272] dark:border-[#444444]'
                }`}>
                  {formData.isPrivate && (
                    <svg width="14" height="10" viewBox="0 0 14 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 5L5 9L13 1" stroke={formData.isPrivate ? (document.documentElement.classList.contains('dark') ? 'black' : 'white') : 'none'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>
              </label>
              <label htmlFor="isPrivate" className="ml-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                Make this project private (only invited members can see it)
              </label>
            </div>

            {/* Team Members Section */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Team Members
                </label>
                <button
                  type="button"
                  onClick={() => setShowMemberSearch(!showMemberSearch)}
                  className="inline-flex items-center px-3 py-1 text-sm text-black dark:text-white hover:text-gray-700 dark:hover:text-gray-300 font-medium"
                >
                  <UserPlusIcon className="w-4 h-4 mr-1" />
                  Add Members
                </button>
              </div>

              {/* Selected Members */}
              {selectedMembers.length > 0 && (
                <div className="mb-4">
                  <div className="flex flex-wrap gap-2">
                    {selectedMembers.map(member => (
                      <div
                        key={member.id}
                        className="relative inline-flex items-center pl-2 pr-4 py-2 bg-gray-100 dark:bg-[#282828] text-gray-800 dark:text-gray-200 rounded-full text-sm"
                      >
                        {member.profileImage?.url ? (
                          <img 
                            src={member.profileImage.url} 
                            alt={member.name}
                            className="w-8 h-8 rounded-full mr-3"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center text-sm font-medium text-gray-600 dark:text-gray-300 mr-3">
                            {(() => {
                              if (member.firstName && member.lastName) {
                                return `${member.firstName.charAt(0)}${member.lastName.charAt(0)}`.toUpperCase();
                              } else if (member.name) {
                                const nameParts = member.name.trim().split(' ');
                                if (nameParts.length >= 2) {
                                  return `${nameParts[0].charAt(0)}${nameParts[nameParts.length - 1].charAt(0)}`.toUpperCase();
                                } else {
                                  return nameParts[0].charAt(0).toUpperCase();
                                }
                              }
                              return 'U';
                            })()}
                          </div>
                        )}
                        <span>{member.name}</span>
                        <button
                          type="button"
                          onClick={() => handleMemberRemove(member.id)}
                          className="absolute -top-1 -right-1 w-5 h-5 bg-gray-500 hover:bg-gray-600 dark:bg-gray-600 dark:hover:bg-gray-700 text-white rounded-full flex items-center justify-center text-xs"
                        >
                          <XMarkIcon className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Member Search */}
              {showMemberSearch && (
                <div className="space-y-3">
                  {/* Search Input - Outside of results container */}
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search team members by name"
                      autoComplete="new-password"
                      className="w-full pl-9 pr-4 py-2 border border-gray-300 dark:border-[#444444] rounded-xl focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent bg-white dark:bg-[#181818] text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                    />
                  </div>

                  {/* Results Container - Only show when there's content */}
                  {(isSearching || (searchTerm && searchResults.length >= 0)) && (
                    <div className="border border-gray-300 dark:border-[#444444] rounded-xl p-4 bg-gray-50 dark:bg-[#181818]">
                      {/* Search Results */}
                      {isSearching && (
                        <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                          Searching...
                        </div>
                      )}

                      {!isSearching && searchTerm && searchResults.length === 0 && (
                        <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                          No members found
                        </div>
                      )}

                      {searchResults.length > 0 && (
                        <div className="max-h-48 overflow-y-auto">
                          {searchResults.map(member => (
                            <button
                              key={member.id}
                              type="button"
                              onClick={() => handleMemberSelect(member)}
                              className="w-full flex items-center px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-[#282828] rounded-xl transition-colors"
                            >
                              {member.profileImage?.url ? (
                                <img 
                                  src={member.profileImage.url} 
                                  alt={member.name}
                                  className="w-8 h-8 rounded-full mr-3"
                                />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center text-sm font-medium text-gray-600 dark:text-gray-300 mr-3">
                                  {(() => {
                                    if (member.firstName && member.lastName) {
                                      return `${member.firstName.charAt(0)}${member.lastName.charAt(0)}`.toUpperCase();
                                    } else if (member.name) {
                                      const nameParts = member.name.trim().split(' ');
                                      if (nameParts.length >= 2) {
                                        return `${nameParts[0].charAt(0)}${nameParts[nameParts.length - 1].charAt(0)}`.toUpperCase();
                                      } else {
                                        return nameParts[0].charAt(0).toUpperCase();
                                      }
                                    }
                                    return 'U';
                                  })()}
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                  {member.name}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                  {member.email}
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* 🚀 NEW: AI Context Features Preview */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  AI Context & Memory
                </label>
                <div className="inline-flex items-center px-3 py-1 text-sm text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 rounded-full">
                  <ChartBarIcon className="w-4 h-4 mr-1" />
                  Auto-enabled
                </div>
              </div>
              
              <div className="border border-gray-300 dark:border-[#444444] rounded-xl p-4 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/10 dark:to-indigo-900/10">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <ChartBarIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white text-sm">Smart Context Building</h4>
                      <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">
                        Your project will automatically build an intelligent context from uploaded documents and conversations.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <DocumentTextIcon className="w-5 h-5 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white text-sm">Document Intelligence</h4>
                      <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">
                        Files you upload will be automatically summarized and made available to the AI for better responses.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <ClockIcon className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white text-sm">Conversation Memory</h4>
                      <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">
                        Past discussions are summarized and remembered, providing continuous context across sessions.
                      </p>
                    </div>
                  </div>
                  
                  <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-600">
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                      <span>Token-optimized • Claude-style intelligence • Auto-updating</span>
                      <span className="bg-white dark:bg-gray-800 px-2 py-1 rounded-full border">
                        ~2k tokens max
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-3 bg-red-100 dark:bg-red-900 border border-red-400 text-red-700 dark:text-red-300 rounded-xl text-sm">
                {error}
              </div>
            )}
          </form>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 dark:border-[#444444]">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!isFormValid || isSubmitting}
            className="inline-flex items-center px-6 py-2 bg-black dark:bg-white hover:bg-gray-100 dark:hover:bg-gray-700 disabled:bg-gray-400 text-white dark:text-black font-medium rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin mr-2"></div>
                Creating...
              </>
            ) : (
              <>
                <CheckIcon className="w-4 h-4 mr-2" />
                Create Project
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateProjectModal; 